import React, { useContext, useState } from "react";
import { Box, Fab, makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { UserDataContext } from "../../Contexts/UserDataContext";
import EntityFormControl from "./EntityFormControl";
import { Add, Search } from "@material-ui/icons";
import { columns, defaultRate, rateDefinition } from "./RateEntityClass";
import Axios from "axios";
import {
  SelectActiveInputComponent,
  SelectStateInputComponent,
  SelectTruckIdInputComponent,
  SelectVendorIdInputComponent,
} from "./InputComponents";
import EntityTableFilter from "./EntityTableFilter";
import EntityTable from "./EntityTable";

const useStyles = makeStyles((theme) => ({
  flexColContainer: {
    display: "flex",
    flexDirection: "column",
  },
  flexColItem: {
    margin: theme.spacing(0, 0, 4, 0),
  },
  flexRowContainer: {
    display: "flex",
    alignItems: "center",
  },
  filter: {
    width: "160px",
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  tableFilter: {
    flexGrow: 1,
  },
  marginLeft: {
    marginLeft: theme.spacing(2),
  },
  marginRight: {
    marginRight: theme.spacing(2),
  },
}));

const RatePage = (props) => {
  const { className, ...rest } = props;
  const classes = useStyles();
  const { userData } = useContext(UserDataContext);
  const [rateList, setRateList] = useState([]);
  const [filterExpression, setFilterExpression] = useState("");
  const [filterParameters, setFilterParameters] = useState({
    state: undefined,
    truckId: undefined,
  });

  const fetchDistinctBranches = async () => {
    const requestActive = {
      method: "GET",
      url: "/branches/search?state=1",
      headers: {
        "Content-Type": "application/json",
        Authorization: `bearer ${userData.token}`,
      },
    };
    const requestWaiting = {
      method: "GET",
      url: "/branches/search?state=2",
      headers: {
        "Content-Type": "application/json",
        Authorization: `bearer ${userData.token}`,
      },
    };

    try {
      const [resActive, resWaiting] = await Promise.all([
        Axios(requestActive),
        Axios(requestWaiting),
      ]);
      // get unique list of active + waiting branches, filter by duplicate shortCode.
      const uniqueList = [
        ...resActive.data.map((branch) => ({ ...branch, state: 1 })),
        ...resWaiting.data.map((branch) => ({ ...branch, state: 2 })),
      ]
        .reduce(
          (acc, cur) =>
            acc.some((branch) => branch.shortCode === cur.shortCode)
              ? acc
              : [...acc, cur],
          []
        )
        .sort((a, b) => {
          if (a.shortCode > b.shortCode) return 1;
          if (a.shortCode < b.shortCode) return -1;
          return 0;
        });
      // uniqueList.forEach((branch) => console.log(branch.shortCode));
      return uniqueList;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRates = async () => {
    const queryString = Object.keys(filterParameters)
      .filter((key) => Boolean(filterParameters[key]))
      .map((key) => `${key}=${filterParameters[key]}`)
      .join("&");

    const request = {
      method: "GET",
      url: `/truckpayments/search?${queryString}`,
      // url: `/users`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `bearer ${userData.token}`,
      },
    };

    try {
      const res = await Axios(request);
      const rateListWthCcArr = res.data.map((rate) => {
        const ccArr =
          rate.truckPaymentBasefees.length > rate.truckPaymentTopups.length
            ? rate.truckPaymentBasefees
            : rate.truckPaymentTopups;
        return {
          ...rate,
          ccArr,
        };
      });
      console.log(rateListWthCcArr);
      setRateList(rateListWthCcArr);
    } catch (error) {
      console.log(error);
    }
  };

  const createRate = async (entity, onSuccessCallback, onFailCallback) => {
    try {
      const branchList = await fetchDistinctBranches();
      // filter the branch by short code to find the cost centre
      const ccArr = entity.ccArr.map((cc) => {
        const [branch] = branchList.filter(
          (branch) => branch.shortCode === cc.shortCode
        );
        const costCentre = cc.isParcel
          ? branch.parcelCostCentre
          : branch.lclLinehaulCostCentre;

        return { ...cc, id: 0, costCentre };
      });

      // only one of these should be sent to the back end
      const truckPaymentBasefees = entity.baseFee ? ccArr : undefined;
      const truckPaymentTopups = entity.topup ? ccArr : undefined;

      const request = {
        method: "POST",
        url: "/truckpayments/add",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
        data: {
          ...entity,
          ccArr,
          truckPaymentBasefees,
          truckPaymentTopups,
        },
      };
      await Axios(request);
      onSuccessCallback();
      fetchRates();
    } catch (error) {
      console.log(error);
      onFailCallback(error.response.data.message);
    }
  };

  const editRate = async (entity, onSuccessCallback, onFailCallback) => {
    try {
      const branchList = await fetchDistinctBranches();
      // filter the branch by short code to find the cost centre
      const ccArr = entity.ccArr.map((cc) => {
        const [branch] = branchList.filter(
          (branch) => branch.shortCode === cc.shortCode
        );
        const costCentre = cc.isParcel
          ? branch.parcelCostCentre
          : branch.lclLinehaulCostCentre;

        return { ...cc, id: 0, costCentre };
      });

      // only one of these should be sent to the back end
      const truckPaymentBasefees = entity.baseFee ? ccArr : undefined;
      const truckPaymentTopups = entity.topup ? ccArr : undefined;

      const request = {
        method: "POST",
        url: "/truckpayments/change",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
        data: {
          ...entity,
          ccArr,
          truckPaymentBasefees,
          truckPaymentTopups,
        },
      };
      await Axios(request);
      onSuccessCallback();
      fetchRates();
    } catch (error) {
      console.log(error);
      onFailCallback(error.response.data.message);
    }
  };

  // const editRate = async (entity, onSuccessCallback, onFailCallback) => {
  //   const request = {
  //     method: "POST",
  //     url: "/truckpayments/change",
  //     headers: {
  //       "Content-Type": "application/json",
  //       Authorization: `bearer ${userData.token}`,
  //     },
  //     data: entity,
  //   };
  //   try {
  //     await Axios(request);
  //     onSuccessCallback();
  //     fetchRates();
  //   } catch (error) {
  //     console.log(error);
  //     onFailCallback(error.response.data.message);
  //   }
  // };

  return (
    <Box className={clsx(className, classes.flexColContainer)} {...rest}>
      <Box className={clsx(classes.flexColItem, classes.flexRowContainer)}>
        <EntityFormControl
          buttonComponent={(buttonProps) => (
            <Fab
              className={clsx(classes.marginRight)}
              variant="extended"
              color="primary"
              {...buttonProps}
            >
              <Add className={classes.extendedIcon} />
              Create
            </Fab>
          )}
          title="Create Rate"
          columns={columns}
          initialData={defaultRate}
          entityDefinition={rateDefinition}
          onSubmit={createRate}
        />
        <SelectStateInputComponent
          className={clsx(classes.filter)}
          name="state"
          variant="outlined"
          label="Status"
          value={filterParameters.state}
          onUpdate={(name, value) =>
            setFilterParameters({ ...filterParameters, [name]: value })
          }
        />
        <SelectTruckIdInputComponent
          className={clsx(classes.filter)}
          name="truckId"
          variant="outlined"
          label="Truck"
          value={filterParameters.truckId}
          onUpdate={(name, value) =>
            setFilterParameters({ ...filterParameters, [name]: value })
          }
        />
        <Fab
          className={clsx(classes.marginLeft, classes.marginRight)}
          onClick={fetchRates}
          variant="extended"
        >
          <Search className={classes.extendedIcon} />
          Search
        </Fab>
        <EntityTableFilter
          className={clsx(classes.tableFilter)}
          expression={filterExpression}
          setExpression={setFilterExpression}
        />
      </Box>

      <EntityTable
        className={clsx(classes.flexColItem, classes.center)}
        entityDefinition={rateDefinition}
        tableColumns={[
          "id",
          "truckId",
          "floatingRate",
          "baseFee",
          "topup",
          "startDate",
          "endDate",
        ]}
        editColumns={columns}
        disabledColumns={["truckId"]}
        rows={rateList}
        formTitle="Edit Rate"
        onEdit={editRate}
        filterExpression={filterExpression}
      />
    </Box>
  );
};

export default RatePage;
